import React, { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider } from '../contexts/AuthContext';
import { ThemeProvider as ThemeContextProvider } from '../contexts/ThemeContext';
import ThemeProvider from '../theme/ThemeProvider';
import { TranscriptionProvider } from '../contexts/TranscriptionContext';
import { DemoProvider } from '../contexts/DemoContext';
import { AISidebarProvider } from '../contexts/AISidebarContext';
import { EmailProvider } from '../contexts/EmailContext';
import { EventProvider } from '../contexts/EventContext';
import ErrorBoundary from '../components/shared/ErrorBoundary';

// Create a stable query client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      suspense: true,
    },
  },
});

// Fallback components for error states
const GlobalErrorFallback = ({ error }) => (
  <div className="p-4 text-red-600">
    <h2>Application Error</h2>
    <pre>{error.message}</pre>
  </div>
);

const AuthErrorFallback = () => (
  <div className="p-4">
    <h2>Authentication Error</h2>
    <p>Please try logging in again</p>
  </div>
);

const FeatureErrorFallback = () => (
  <div className="p-4">
    <h2>Feature Temporarily Unavailable</h2>
    <p>Please try again later</p>
  </div>
);

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

export function AppProviders({ children }) {
  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
      <QueryClientProvider client={queryClient}>
        {/* Global state providers */}
        <ThemeContextProvider>
          <ThemeProvider>
            <ErrorBoundary FallbackComponent={AuthErrorFallback}>
              <AuthProvider>
                <Router>
                  {/* Feature-specific providers */}
                  <ErrorBoundary FallbackComponent={FeatureErrorFallback}>
                    <Suspense fallback={<LoadingSpinner />}>
                      <TranscriptionProvider>
                        <DemoProvider>
                          <AISidebarProvider>
                            <EmailProvider>
                              <EventProvider>
                                {children}
                              </EventProvider>
                            </EmailProvider>
                          </AISidebarProvider>
                        </DemoProvider>
                      </TranscriptionProvider>
                    </Suspense>
                  </ErrorBoundary>
                </Router>
              </AuthProvider>
            </ErrorBoundary>
          </ThemeProvider>
        </ThemeContextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
} 